/**
 * F-27 selection context toolbar (Miro-style floating pill).
 *
 * Rendered OUTSIDE the transformed camera layer, in screen coordinates
 * computed from the shared boardToScreen transform — subscribing to the
 * camera store makes it track pan/zoom exactly, at constant screen size
 * (the "counter-scaled affordance" contract without an inverse transform).
 *
 * Anchoring:
 *  - cell ranges: the grid publishes a CellsToolbarSelection (board-space
 *    bounding rect from its live layout pass) into the SelectionUiStore —
 *    null while editing / drag-selecting / resizing / reordering, which is
 *    what hides the pill during those interactions;
 *  - elements: derived here from the selected element id + elementsVersion.
 *
 * Placement (pure math in placement.ts): above the selection with an 8 px
 * gap, over the POINTER that made the selection when the grid published one
 * (centred over the selection otherwise), flipped below when clipped at the
 * viewport top. Hidden while
 * panning (drag/space pans; wheel pans just track — the pill follows the
 * camera perfectly, so hiding would only flicker), while an element drag is
 * in flight, and while the board is frozen (F-45 read-only).
 *
 * Focus (F-27 UX): the pill never steals keyboard focus from the grid —
 * mousedown is prevented for everything except the palette's hex input; Esc
 * closes an open popover BEFORE the grid's Esc-clears-selection can see the
 * key (window capture listener), returning focus to the grid.
 */
import { boardToScreen } from "@kola/shared";
import { useEffect, useMemo, useState, type MutableRefObject } from "react";
import { createStore, type StoreApi } from "zustand/vanilla";
import { useStore } from "zustand";

import type { CanvasStore } from "../../core/canvas.js";
import type { BoardSession } from "../../core/session.js";
import {
  toolbarActionsFor,
  type CellsToolbarSelection,
  type ToolbarActionContext,
  type ToolbarSelection,
} from "../../registries/toolbar-actions/index.js";
import type { GridApi, GridLayoutSnapshot } from "../board/GridTable.js";
import { resolveElementRect } from "../elements/contained.js";
import {
  placeToolbar,
  popoverSide,
  TOOLBAR_GAP_PX,
  TOOLBAR_HEIGHT_PX,
  TOOLBAR_TOP_INSET_PX,
} from "./placement.js";
// Side-effect import: registers the built-in v1 actions (idempotent).
import "./actions.js";

/* ------------------------------------------------------------------ */
/* Shared toolbar UI state (BoardCanvas owns one instance)             */
/* ------------------------------------------------------------------ */

/**
 * Tiny store so the grid/elements layers can publish interaction state
 * without re-rendering the canvas shell (BoardCanvas subscribes to nothing
 * here; only the toolbar itself does).
 */
export interface SelectionUiState {
  /** Grid-published cell-selection anchor; null hides the cells toolbar. */
  cellAnchor: CellsToolbarSelection | null;
  /** A pointer pan drag (bg drag, space+drag, middle-drag) is in flight. */
  panning: boolean;
  /** A floating-element move/resize drag is in flight. */
  elementDragging: boolean;
  setCellAnchor(anchor: CellsToolbarSelection | null): void;
  setPanning(panning: boolean): void;
  setElementDragging(dragging: boolean): void;
}

export type SelectionUiStore = StoreApi<SelectionUiState>;

export function createSelectionUiStore(): SelectionUiStore {
  return createStore<SelectionUiState>((set) => ({
    cellAnchor: null,
    panning: false,
    elementDragging: false,
    setCellAnchor: (cellAnchor) => set({ cellAnchor }),
    setPanning: (panning) => set({ panning }),
    setElementDragging: (elementDragging) => set({ elementDragging }),
  }));
}

/* ------------------------------------------------------------------ */
/* The toolbar                                                         */
/* ------------------------------------------------------------------ */

export interface SelectionToolbarProps {
  session: BoardSession;
  canvas: CanvasStore;
  ui: SelectionUiStore;
  /** F-36: the grid's latest layout pass — resolves a contained (cell-anchored)
   * element's on-screen rect so the pill tracks it as its cell reflows. */
  gridLayout: GridLayoutSnapshot | null;
  /** Currently-selected floating element (BoardCanvas owns this state). */
  selectedElementId: string | null;
  /** Space-pan chord is held (hide, like an active pan). */
  spaceHeld: boolean;
  /**
   * F-50 comment mode is active (hide: its capture layer covers the whole
   * canvas, so a visible-but-unclickable pill would just mislead).
   */
  commentMode?: boolean;
  /** Focus handoff back to the grid when a popover closes via Esc. */
  gridApi?: MutableRefObject<GridApi | null>;
}

export function SelectionToolbar({
  session,
  canvas,
  ui,
  gridLayout,
  selectedElementId,
  spaceHeld,
  commentMode = false,
  gridApi,
}: SelectionToolbarProps) {
  const camera = useStore(canvas, (s) => s.camera);
  const viewport = useStore(canvas, (s) => s.viewport);
  const cellAnchor = useStore(ui, (s) => s.cellAnchor);
  const panning = useStore(ui, (s) => s.panning);
  const elementDragging = useStore(ui, (s) => s.elementDragging);
  const frozen = useStore(session.store, (s) => s.frozen);
  const elementsVersion = useStore(session.store, (s) => s.elementsVersion);

  const [openPopoverId, setOpenPopoverId] = useState<string | null>(null);

  // Element selection wins (selecting an element already clears the grid
  // selection and vice versa — BoardCanvas keeps them mutually exclusive).
  const selection: ToolbarSelection | null = useMemo(() => {
    if (selectedElementId !== null) {
      void elementsVersion; // re-derive when any element changes
      const element = session.board.getElement(selectedElementId);
      if (element === undefined) return null;
      // A contained element's real rect comes from its cell (F-36), not the
      // x/y cache — resolve against the current grid layout.
      return {
        kind: "element",
        elementId: selectedElementId,
        rect: resolveElementRect(element, gridLayout),
      };
    }
    return cellAnchor;
  }, [session, selectedElementId, elementsVersion, cellAnchor, gridLayout]);

  // Close the popover when the selection TARGET changes — keyed on stable
  // ids, NOT object identity: applying a palette color re-publishes the
  // anchor (content tick) and must not slam the palette shut.
  const targetKey =
    selection === null
      ? null
      : selection.kind === "element"
        ? `e:${selection.elementId}`
        : `c:${selection.sheetId}:${selection.rowIds.join(",")}:${selection.colIds.join(",")}`;
  useEffect(() => {
    setOpenPopoverId(null);
  }, [targetKey]);

  const hidden =
    selection === null || frozen || panning || spaceHeld || elementDragging || commentMode;

  // A popover must not survive a hide (pan with the palette open) and then
  // ghost back on the same selection.
  useEffect(() => {
    if (hidden) setOpenPopoverId(null);
  }, [hidden]);

  // F-27 UX: Esc closes the popover BEFORE the grid's Escape handler clears
  // the selection. Window CAPTURE phase runs ahead of React's root-attached
  // handlers; stopPropagation keeps the event from ever reaching them.
  const popoverOpen = openPopoverId !== null;
  useEffect(() => {
    if (!popoverOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      setOpenPopoverId(null);
      // The hex input may hold focus — hand the keyboard back to the grid.
      gridApi?.current?.focus();
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [popoverOpen, gridApi]);

  if (hidden) return null;

  const ctx: ToolbarActionContext = {
    session,
    selection,
    closePopover: () => setOpenPopoverId(null),
  };

  // `appliesTo` filters by selection kind; `visible` filters by what is
  // actually selected (F-70's translate icon: exactly one non-source column).
  // An all-hidden pill renders nothing rather than an empty rounded box.
  const actions = toolbarActionsFor(selection.kind).filter((a) => a.visible?.(ctx) ?? true);
  if (actions.length === 0) return null;

  // Board → screen: the toolbar div lives in the (non-transformed) canvas
  // container, whose origin is exactly the camera viewport origin.
  const topLeft = boardToScreen(camera, { x: selection.rect.x, y: selection.rect.y });
  // The pointer that made a cell selection, in screen space — it is stored in
  // BOARD units so it pans and zooms with the cells it was over.
  const pointerX =
    selection.kind === "cells" && selection.pointer !== undefined
      ? boardToScreen(camera, selection.pointer).x
      : null;
  const place = placeToolbar(
    {
      left: topLeft.x,
      top: topLeft.y,
      width: selection.rect.w * camera.zoom,
      height: selection.rect.h * camera.zoom,
    },
    viewport,
    TOOLBAR_HEIGHT_PX,
    TOOLBAR_GAP_PX,
    TOOLBAR_TOP_INSET_PX,
    pointerX,
  );

  const openAction = actions.find((a) => a.id === openPopoverId);

  return (
    <div
      className="selection-toolbar"
      style={{ left: place.centerX, top: place.top }}
      data-placement={place.placement}
      onMouseDown={(event) => {
        // Never steal focus from the grid — except for the palette's hex
        // input, which needs focus to type into.
        const target = event.target as HTMLElement;
        if (target.tagName !== "INPUT") event.preventDefault();
      }}
    >
      <div
        className="st-pill"
        role="toolbar"
        aria-label={selection.kind === "cells" ? "Cell actions" : "Element actions"}
        style={{ height: TOOLBAR_HEIGHT_PX }}
      >
        {actions.map((action) => {
          const enabled = action.enabled?.(ctx) ?? true;
          const tip = typeof action.tooltip === "function" ? action.tooltip(ctx) : action.tooltip;
          return (
            <button
              key={action.id}
              type="button"
              className={`st-btn${openPopoverId === action.id ? " active" : ""}`}
              data-tip={tip}
              aria-label={tip}
              disabled={!enabled}
              onClick={() => {
                if (action.popover !== undefined) {
                  setOpenPopoverId((prev) => (prev === action.id ? null : action.id));
                } else {
                  action.run?.(ctx);
                }
              }}
            >
              {typeof action.icon === "function" ? action.icon(ctx) : action.icon}
            </button>
          );
        })}
      </div>
      {openAction?.popover !== undefined && (
        // Popovers open AWAY from the selection (above the pill when the pill
        // is above, below when flipped) so they don't cover what they style —
        // unless the viewport top would clip them (popoverSide falls back to
        // below-the-pill: covering the selection beats being unreachable).
        <div className={`st-popover st-popover-${popoverSide(place.placement, place.top)}`}>
          {openAction.popover(ctx)}
        </div>
      )}
    </div>
  );
}
